import DeleteIcon from "@assets/icons/delete.svg";
import LogoutIcon from "@assets/icons/logout.svg";
import ResendIcon from "@assets/icons/set.svg";

import { useI18n } from "@core/i18n/i18n";
import { healthcareTeamManager } from "@core/service-container";
import {
	HealthcareTeamMember,
	HealthcareTeamMemberStatus,
	isUnregisteredHealthcareTeamMember,
	RegisteredHealthcareTeamMember,
	UnregisteredHealthcareTeamMember,
} from "@domain/healthcare-team/healthcare-team";
import {
	sendAnalyticsEventClickCtaLeaveHct,
	sendAnalyticsEventDeleteColleague,
	sendAnalyticsEventResendInvitation,
} from "@domain/healthcare-team/healthcare-team-analytics";
import { Patient } from "@domain/patient/patient";
import { User } from "@domain/user/user";
import styled from "@emotion/styled";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { MenuButton, MenuItemProps } from "@ui/components/common/menu-button";
import { ToastContext } from "@ui/components/common/toast-provider";
import { colors } from "@ui/style/colors";
import { isNil } from "@utils/assert";
import { formatName } from "@utils/string-utils";
import React, { useCallback, useContext, useMemo } from "react";

interface HealthcareTeamMemberActionButtonProps {
	user: User;
	patient: Patient;
	healthcareTeamMember: HealthcareTeamMember;
}

export const HealthcareTeamMemberActionButton = ({
	user,
	patient,
	healthcareTeamMember,
}: HealthcareTeamMemberActionButtonProps): JSX.Element => {
	const { format } = useI18n();
	const dialogContext = useContext(DialogContext);
	const { showToast } = useContext(ToastContext);

	const reopenInviteUnregisteredPtDialog = useCallback(
		async (invitedPt: UnregisteredHealthcareTeamMember) => {
			dialogContext?.openModal(ModalId.HealthcareTeamUnregisteredInvite, {
				invitedPt: { email: invitedPt.email, invitationId: invitedPt.ptInvitationId },
				invitingPt: user,
				patient,
			});
			sendAnalyticsEventResendInvitation(patient, invitedPt);
		},
		[dialogContext, patient, user],
	);

	const deleteUnregisteredPt = useCallback(
		async (invitedPt: UnregisteredHealthcareTeamMember) => {
			await healthcareTeamManager.deleteMember(patient.id, invitedPt.id);
			showToast(
				format("patientDetails.healthcareTeam.actionButton.delete.toastSuccess.title"),
				format("patientDetails.healthcareTeam.actionButton.delete.toastSuccess.message", {
					invitedPtName: invitedPt.email,
					patientName: formatName(patient),
				}),
				"success",
			);
			sendAnalyticsEventDeleteColleague(patient, invitedPt);
		},
		[format, patient, showToast],
	);

	const resendInvitation = useCallback(
		async (invitedPt: RegisteredHealthcareTeamMember) => {
			try {
				await healthcareTeamManager.resendInvitation(patient.id, healthcareTeamMember.id);
				showToast(
					format("patientDetails.healthcareTeam.actionButton.resend.toastSuccess.title"),
					format("patientDetails.healthcareTeam.actionButton.resend.toastSuccess.message", {
						email: invitedPt.email,
					}),
					"success",
				);
				sendAnalyticsEventResendInvitation(patient, invitedPt);
			} catch (e) {
				console.error("Failed to resend invite", e);
			}
		},
		[format, healthcareTeamMember.id, patient, showToast],
	);

	const leaveHCT = useCallback(async () => {
		dialogContext?.openModal(ModalId.HealthcareTeamLeave, {
			patient,
		});
		sendAnalyticsEventClickCtaLeaveHct();
	}, [dialogContext, patient]);

	const menuItems = useMemo(() => {
		const items: MenuItemProps[] = [];

		if (isUnregisteredHealthcareTeamMember(healthcareTeamMember)) {
			// Here the member can be either unregistered OR registered but unvalidated.
			// We only allow to resend the invitation if the member is unregistered
			if (isNil(healthcareTeamMember.ptId)) {
				items.push({
					text: format("patientDetails.healthcareTeam.actionButton.resend"),
					icon: <ResendIcon />,
					onClick: () => reopenInviteUnregisteredPtDialog(healthcareTeamMember),
				});
			}
			items.push({
				text: format("patientDetails.healthcareTeam.actionButton.delete"),
				icon: <DeleteIcon />,
				destructive: true,
				onClick: () => deleteUnregisteredPt(healthcareTeamMember),
			});
		} else if (healthcareTeamMember.status !== HealthcareTeamMemberStatus.Confirmed) {
			items.push({
				text: format("patientDetails.healthcareTeam.actionButton.resend"),
				icon: <ResendIcon />,
				onClick: () => resendInvitation(healthcareTeamMember),
			});
		} else if (user.id === healthcareTeamMember.ptId) {
			items.push({
				text: format("patientDetails.healthcareTeam.actionButton.leave"),
				icon: <LogoutIcon />,
				onClick: leaveHCT,
				destructive: true,
			});
		}
		return items;
	}, [
		deleteUnregisteredPt,
		format,
		healthcareTeamMember,
		leaveHCT,
		reopenInviteUnregisteredPtDialog,
		resendInvitation,
		user.id,
	]);

	if (menuItems.length === 0) return <></>;
	return <ActionButton menuId={healthcareTeamMember.id} menuItems={menuItems} />;
};

const ActionButton = styled(MenuButton)`
	.menu-button {
		background-color: ${colors.transparent};
		&.hover {
			background-color: ${colors.grey10};
		}
	}
`;
